import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { Box, CircularProgress, Container } from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  BrandSectionContainer,
  BrandHeader,
  ViewTemplatesButton,
} from 'src/mentor/components/MentorPrescriptionTemplates/MentorPrescriptionTemplatesStyles'
import {
  ClinicItem,
  ClinicChoosingContainer,
} from 'src/mentor/components/MentorClinicBookingsPage/MentorClinicBookingStyles'

const PageContainer = styled(Container)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(3, 2),
  background: 'linear-gradient(180deg, rgba(249, 250, 251, 0.5) 0%, rgba(243, 244, 246, 0.3) 100%)',
  minHeight: 'calc(100vh - 200px)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1.5),
  },
}))

const PageMainContentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1400px',
  margin: '0 auto',
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}))

export default function SupportPrescriptionTemplates() {
  const navigate = useNavigate()
  const { mentorSlug } = useParams()
  const [associatedClinics, setAssociatedClinics] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const clinicBrands = useMemo(() => {
    const clinicBrandIdToClinicBrandMap = {}
    associatedClinics?.forEach((association) => {
      if (association?.clinicBrandId) {
        clinicBrandIdToClinicBrandMap[association.clinicBrandId] = {
          id: association.clinicBrandId,
          clinicBrandName: association.clinicBrandName,
        }
      }
    })
    return Object.values(clinicBrandIdToClinicBrandMap)
  }, [associatedClinics])

  const clinicLocationOptions = useMemo(() => {
    return clinicBrands?.map((clinicBrand) => ({
      label: clinicBrand?.clinicBrandName || clinicBrand?.title || '--',
      options: associatedClinics?.filter(
        (association) => association?.clinicBrandId === clinicBrand?.id,
      ),
    }))
  }, [associatedClinics, clinicBrands])

  async function getAssociatedClinics() {
    setIsLoading(true)
    try {
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/doctor/clinic-associations',
        { 'mentor-slug': mentorSlug },
        undefined,
        undefined,
        true,
      )
      if (resp && isCommonResponseSuccessfulV2(resp)) {
        const dat = getDataMapFromCommonResponse(resp)?.clinic
        setAssociatedClinics(dat || [])
      }
    } catch (error) {
      console.error('Error fetching clinic associations:', error)
      setAssociatedClinics([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (mentorSlug) {
      getAssociatedClinics()
    }
  }, [mentorSlug])

  return (
    <>
      <SupportHeader heading="Prescription Templates by Clinic" showBackButton />
      <PageContainer>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <PageMainContentContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              {clinicLocationOptions?.map((brandGroup, brandIndex) => (
                <BrandSectionContainer key={brandIndex}>
                  <BrandHeader>{brandGroup?.label}</BrandHeader>
                  <ClinicChoosingContainer>
                    {brandGroup?.options?.map((clinic, clinicIndex) => (
                      <ClinicItem key={clinicIndex} style={{ cursor: 'default' }}>
                        <div style={{ marginBottom: '24px', fontSize: '16px' }}>
                          <b>{clinic?.name || clinic?.title}</b>
                        </div>
                        <ViewTemplatesButton
                          onClick={() => navigate(`${clinic?.id}`)}
                        >
                          View Prescription Templates
                        </ViewTemplatesButton>
                      </ClinicItem>
                    ))}
                  </ClinicChoosingContainer>
                </BrandSectionContainer>
              ))}
              {!isLoading && clinicLocationOptions?.length === 0 && (
                <Box
                  sx={{
                    textAlign: 'center',
                    padding: 4,
                    color: 'text.secondary',
                  }}
                >
                  No clinic associations found for this doctor.
                </Box>
              )}
            </div>
          </PageMainContentContainer>
        )}
      </PageContainer>
    </>
  )
}

